<template>
  <section class="economy-section">
    <div class="economy-container">
      <div class="economy-header">
        <h2 class="economy-title">{{ translations[locale].title }}</h2>
        <p class="economy-subtitle">{{ translations[locale].subtitle }}</p>
      </div>

      <div class="economy-content">
        <div class="economy-image">
          <div ref="chartRef" class="pie-chart"></div>
        </div>
        <div class="economy-details">
          <h3 class="details-title">{{ translations[locale].details.title }}</h3>
          <div class="token-distribution">
            <div class="distribution-item">
              <span class="item-name">{{ translations[locale].details.ecosystem }}</span>
              <span class="item-percentage">30%</span>
              <div class="progress-bar">
                <div class="progress" style="width: 30%"></div>
              </div>
            </div>
            <div class="distribution-item">
              <span class="item-name">{{ translations[locale].details.team }}</span>
              <span class="item-percentage">20%</span>
              <div class="progress-bar">
                <div class="progress" style="width: 20%"></div>
              </div>
            </div>
            <div class="distribution-item">
              <span class="item-name">{{ translations[locale].details.private }}</span>
              <span class="item-percentage">15%</span>
              <div class="progress-bar">
                <div class="progress" style="width: 15%"></div>
              </div>
            </div>
            <div class="distribution-item">
              <span class="item-name">{{ translations[locale].details.public }}</span>
              <span class="item-percentage">25%</span>
              <div class="progress-bar">
                <div class="progress" style="width: 25%"></div>
              </div>
            </div>
            <div class="distribution-item">
              <span class="item-name">{{ translations[locale].details.community }}</span>
              <span class="item-percentage">10%</span>
              <div class="progress-bar">
                <div class="progress" style="width: 10%"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import * as echarts from 'echarts'
import { useStore } from '@/stores/language'

const { t } = useI18n()
const chartRef = ref(null)
let chart = null

const store = useStore()
const locale = computed(() => store.locale)

const translations = {
  zh: {
    title: '代幣經濟模型',
    subtitle: 'KOMA 代幣是生態系統的核心，用於治理、質押和交易手續費',
    details: {
      title: '代幣分配詳情',
      ecosystem: '生態基金',
      team: '團隊持有',
      private: '私募',
      public: '公募',
      community: '社區激勵'
    }
  },
  en: {
    title: 'Token Economy',
    subtitle: 'KOMA token is the core of the ecosystem, used for governance, staking, and transaction fees',
    details: {
      title: 'Token Distribution Details',
      ecosystem: 'Ecosystem Fund',
      team: 'Team Holdings',
      private: 'Private Sale',
      public: 'Public Sale',
      community: 'Community Incentives'
    }
  }
}

const initChart = () => {
  if (!chartRef.value) return

  chart = echarts.init(chartRef.value)

  const option = {
    tooltip: {
      trigger: 'item',
      formatter: '{b}: {c}%'
    },
    legend: {
      show: false
    },
    series: [
      {
        name: 'Token Distribution',
        type: 'pie',
        radius: ['50%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: true,
          position: 'outside',
          formatter: '{b}\n{c}%',
          fontSize: 14,
          fontWeight: 'normal',
          color: '#333333'
        },
        labelLine: {
          show: true,
          length: 15,
          length2: 10,
          lineStyle: {
            color: '#333333'
          }
        },
        data: [
          {
            value: 30,
            name: translations[locale.value].details.ecosystem,
            itemStyle: { color: '#5470c6' }
          },
          {
            value: 20,
            name: translations[locale.value].details.team,
            itemStyle: { color: '#91cc75' }
          },
          {
            value: 15,
            name: translations[locale.value].details.private,
            itemStyle: { color: '#fac858' }
          },
          {
            value: 25,
            name: translations[locale.value].details.public,
            itemStyle: { color: '#ee6666' }
          },
          {
            value: 10,
            name: translations[locale.value].details.community,
            itemStyle: { color: '#73c0de' }
          }
        ]
      }
    ]
  }

  chart.setOption(option)
}

// 监听语言变化，更新图表
watch(locale, () => {
  if (chart) {
    chart.setOption({
      series: [{
        data: [
          {
            value: 30,
            name: translations[locale.value].details.ecosystem,
            itemStyle: { color: '#5470c6' }
          },
          {
            value: 20,
            name: translations[locale.value].details.team,
            itemStyle: { color: '#91cc75' }
          },
          {
            value: 15,
            name: translations[locale.value].details.private,
            itemStyle: { color: '#fac858' }
          },
          {
            value: 25,
            name: translations[locale.value].details.public,
            itemStyle: { color: '#ee6666' }
          },
          {
            value: 10,
            name: translations[locale.value].details.community,
            itemStyle: { color: '#73c0de' }
          }
        ]
      }]
    })
  }
})

const handleResize = () => {
  chart && chart.resize()
}

onMounted(() => {
  initChart()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  if (chart) {
    chart.dispose()
    chart = null
  }
  window.removeEventListener('resize', handleResize)
})
</script>

<style lang="scss" scoped>
.economy-section {
  padding: 96px 0;
  background: #F9FAFB;
  position: relative;
}

.economy-container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 32px;
}

.economy-header {
  text-align: center;
  margin-bottom: 48px;
}

.economy-title {
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 700;
  line-height: 1.2;
  color: #F8FAFC;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #60A5FA 0%, #A78BFA 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
  letter-spacing: 0.05em;
}

.economy-subtitle {
  font-size: clamp(0.875rem, 1.5vw, 1rem);
  color: #4B5563;
  line-height: 1.5;
  max-width: 672px;
  margin: 0 auto;
}

.economy-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: center;
}

.economy-image {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  aspect-ratio: 1;
}

.pie-chart {
  width: 100%;
  height: 100%;
}

.economy-details {
  padding: 20px;
}

.details-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #F8FAFC;
  margin-bottom: 2rem;
}

.token-distribution {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.distribution-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.item-name {
  font-size: 1rem;
  font-weight: 500;
  color: #F8FAFC;
  min-width: 80px;
  flex-shrink: 0;
}

.item-percentage {
  font-size: 1rem;
  color: #6366F1;
  font-weight: 400;
  flex-shrink: 0;
}

.progress-bar {
  flex: 1;
  min-width: 0;
  height: 8px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.progress {
  height: 100%;
  background: #6366F1;
  border-radius: 4px;
  transition: width 0.3s ease;
}

// 响应式设计
@media screen and (max-width: 1200px) {
  .economy-content {
    gap: 24px;
  }
}

@media screen and (max-width: 768px) {
  .economy-section {
    padding: 60px 0;
  }

  .economy-container {
    padding: 0 24px;
  }

  .economy-content {
    grid-template-columns: 1fr;
  }

  .economy-image {
    max-width: 300px;
  }
}

@media screen and (max-width: 480px) {
  .economy-section {
    padding: 40px 0;
  }

  .economy-container {
    padding: 0 16px;
  }

  .economy-details {
    padding: 16px;
  }

  .distribution-item {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  .item-name {
    min-width: 60px;
    font-size: 0.875rem;
  }

  .item-percentage {
    font-size: 0.875rem;
  }

  .progress-bar {
    width: auto;
    min-width: 0;
  }

  .economy-image {
    // max-width: 250px;
  }
}

// 深色模式支持
@media (prefers-color-scheme: dark) {
  .economy-section {
    background: #1F2937;
  }

  .economy-subtitle {
    color: #9CA3AF;
  }

  .progress-bar {
    background: rgba(99, 102, 241, 0.2);
  }
}
</style>